<template>
  <div class="expenses">
    <div class="header">
      <h2>지출 내역</h2>
      <button @click="showExpenseForm" class="add-btn">등록</button>
    </div>
    <div class="filter-section">
      <div class="filter-group">
        <select v-model="selectedCategory">
          <option value="">전체 항목</option>
          <option 
            v-for="category in categories" 
            :key="category.id" 
            :value="category.name"
          >
            {{ category.name }}
          </option>
        </select>

        <select v-model="selectedPeriod" class="period-select">
          <option value="all">전체 기간</option>
          <option value="today">오늘</option>
          <option value="week">이번 주</option>
          <option value="month">이번 달</option>
          <option value="custom">직접 선택</option>
        </select>

        <div v-if="selectedPeriod === 'custom'" class="date-range">
          <input 
            type="date" 
            v-model="dateRange.start"
            :max="dateRange.end"
          >
          <span>~</span>
          <input 
            type="date" 
            v-model="dateRange.end"
            :min="dateRange.start"
          >
        </div>
      </div>
      <div class="total-amount">
        선택 항목 총액: {{ formatNumber(getCategoryTotal) }}원
      </div>
    </div>
    <div class="expense-list">
      <div v-if="filteredExpenses.length === 0" class="no-expenses">
        등록된 지출 내역이 없습니다.
      </div>
      <div v-else>
        <div 
          v-for="expense in filteredExpenses" 
          :key="expense.id" 
          class="expense-item"
          :class="{ 'selected': selectedExpenseId === expense.id }"
          @click="selectExpense(expense)"
        >
          <div class="expense-content">
            <div class="expense-category">{{ expense.category }}</div>
            <div class="expense-amount">{{ formatNumber(expense.amount) }}원</div>
            <div class="expense-date">{{ formatDate(expense.date) }}</div>
          </div>
          <div v-if="selectedExpenseId === expense.id" class="action-buttons">
            <button @click.stop="editExpense(expense)" class="edit-btn">수정</button>
            <button @click.stop="deleteExpense(expense.id)" class="delete-btn">삭제</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 지출 등록/수정 모달 -->
    <expense-form
      v-if="isExpenseFormVisible"
      :expense-to-edit="expenseToEdit"
      @close="hideExpenseForm"
    />
  </div>
</template>

<script>
import { expenseStore } from '../store/expense'
import ExpenseForm from './ExpenseForm.vue'

export default {
  name: 'ExpenseList',
  components: {
    ExpenseForm
  },
  data() {
    return {
      newExpense: {
        category: '',
        amount: '',
        date: ''
      },
      selectedCategory: '',
      selectedPeriod: 'all',
      dateRange: {
        start: '',
        end: ''
      },
      loading: false,
      error: null,
      editingId: null,
      editingExpense: {
        category: '',
        amount: '',
        date: ''
      },
      isExpenseFormVisible: false,
      selectedExpenseId: null,
      expenseToEdit: null
    }
  },
  computed: {
    categories() {
      return expenseStore.categories.value
    },
    expenses() {
      return expenseStore.expenses.value
    },
    filteredExpenses() {
      let filtered = this.expenses

      // 카테고리 필터링
      if (this.selectedCategory) {
        filtered = filtered.filter(expense => 
          expense.category === this.selectedCategory
        )
      }

      // 기간 필터링
      filtered = filtered.filter(expense => {
        const expenseDate = new Date(expense.date)
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        switch (this.selectedPeriod) {
          case 'today':
            return expenseDate >= today
          case 'week': {
            const weekStart = new Date(today)
            weekStart.setDate(today.getDate() - today.getDay())
            return expenseDate >= weekStart
          }
          case 'month': {
            const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
            return expenseDate >= monthStart
          }
          case 'custom': {
            if (!this.dateRange.start || !this.dateRange.end) return true
            
            // 날짜 비교 로직 수정
            const start = new Date(this.dateRange.start)
            start.setHours(0, 0, 0, 0)
            
            const end = new Date(this.dateRange.end)
            end.setHours(23, 59, 59, 999)
            
            // 시간을 제외한 날짜만으로 비교
            const expenseDateOnly = new Date(expenseDate)
            expenseDateOnly.setHours(0, 0, 0, 0)
            
            return expenseDateOnly >= start && expenseDateOnly <= end
          }
          default:
            return true
        }
      })

      // 날짜순 정렬
      return filtered.sort((a, b) => new Date(b.date) - new Date(a.date))
    },
    getCategoryTotal() {
      return this.filteredExpenses.reduce(
        (total, expense) => total + expense.amount, 
        0
      )
    }
  },
  created() {
    // 초기 날짜 범위 설정
    const today = new Date()
    this.dateRange.end = today.toISOString().split('T')[0]
    today.setDate(today.getDate() - 30) // 기본값 30일
    this.dateRange.start = today.toISOString().split('T')[0]
  },
  methods: {
    selectExpense(expense) {
      this.selectedExpenseId = this.selectedExpenseId === expense.id ? null : expense.id
    },
    editExpense(expense) {
      this.expenseToEdit = expense
      this.showExpenseForm()
    },
    async deleteExpense(id) {
      if (confirm('이 지출 내역을 삭제하시겠습니까?')) {
        try {
          await expenseStore.deleteExpense(id)
          this.selectedExpenseId = null
        } catch (error) {
          console.error('지출 내역 삭제 실패:', error)
        }
      }
    },
    showExpenseForm() {
      this.isExpenseFormVisible = true
    },
    hideExpenseForm() {
      this.isExpenseFormVisible = false
      this.expenseToEdit = null
      this.selectedExpenseId = null
    },
    formatDate(date) {
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatNumber(number) {
      return number.toLocaleString()
    },
    startEdit(expense) {
      this.editingId = expense.id
      this.editingExpense = {
        category: expense.category,
        amount: expense.amount,
        date: expense.date
      }
    },
    async updateExpense(id) {
      if (!this.editingExpense.category || !this.editingExpense.amount || !this.editingExpense.date || this.loading) return

      try {
        this.loading = true
        await expenseStore.updateExpense(id, this.editingExpense)
        this.cancelEdit()
      } catch (error) {
        this.error = '지출 내역 수정 실패'
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    cancelEdit() {
      this.editingId = null
      this.editingExpense = {
        category: '',
        amount: '',
        date: ''
      }
    }
  }
}
</script>

<style scoped>
.expenses {
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 20px;
}

.add-btn {
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

.expense-form {
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 8px;
}

.expense-form select,
.expense-form input,
.expense-form button {
  padding: 8px;
  font-size: 16px;
}

.filter-section {
  margin: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: white;
  border-radius: 4px;
  font-size: 16px;
}

.filter-section select {
  padding: 8px;
  min-width: 150px;
  font-size: 16px;
}

.total-amount {
  font-weight: bold;
  color: #2196F3;
  font-size: 18px;
}

.expense-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.expense-item {
  background-color: white;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.expense-item:hover {
  background-color: #f5f5f5;
}

.expense-item.selected {
  background-color: #e3f2fd;
  border: 1px solid #2196F3;
}

.expense-content {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 12px;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 8px;
  padding: 0 12px 12px 12px;
}

.edit-btn,
.delete-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.edit-btn {
  background-color: #2196F3;
  color: white;
}

.edit-btn:hover {
  background-color: #1976D2;
}

.delete-btn {
  background-color: #ff4444;
  color: white;
}

.delete-btn:hover {
  background-color: #cc0000;
}

.expense-category {
  font-size: 14px;
}

.expense-amount {
  text-align: right;
  font-weight: bold;
  font-size: 14px;
  color: #f44336;
}

.expense-date {
  text-align: right;
  color: #666;
  font-size: 13px;
}

.no-expenses {
  text-align: center;
  padding: 20px;
  color: #666;
  background-color: white;
  border-radius: 4px;
}

/* 모바일 최적화 */
@media (max-width: 600px) {
  .expenses {
    padding: 15px;
  }

  .header {
    flex-direction: row;
    gap: 10px;
    align-items: center;
  }

  .header h2 {
    font-size: 22px;
  }

  .add-btn {
    width: auto;
    padding: 8px 16px;
    font-size: 18px;
  }

  .expense-form {
    grid-template-columns: 1fr;
  }

  .expense-form select,
  .expense-form input,
  .expense-form button {
    font-size: 18px;
    padding: 12px;
  }

  .filter-section {
    flex-direction: column;
    gap: 10px;
  }

  .filter-section select {
    width: 100%;
    font-size: 18px;
    padding: 12px;
  }

  .total-amount {
    font-size: 20px;
    text-align: center;
    width: 100%;
  }

  .expense-content {
    font-size: 16px;
    gap: 8px;
  }

  .action-buttons {
    padding: 0 12px 12px 12px;
  }

  .edit-btn,
  .delete-btn {
    flex: 1;
    padding: 10px;
    font-size: 16px;
  }
}
</style> 